import {
  Drawer as ChakraDrawer,
  Portal,
  type DrawerRootProps as ChakraDrawerRootProps,
  type DrawerHeaderProps as ChakraDrawerHeaderProps,
  type DrawerBodyProps as ChakraDrawerBodyProps,
  type DrawerFooterProps as ChakraDrawerFooterProps,
  type DrawerCloseTriggerProps as ChakraDrawerCloseTriggerProps,
} from "@chakra-ui/react";
import Box from "@ui-kit/layout/Box/Box.web";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import React, { Children, cloneElement, isValidElement, useId } from "react";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import CloseButton from "@ui-kit/form/CloseButton/CloseButton.web";
import { useTranslation } from "@shared/utils/internationalization";
import { bodyBackgroundColor } from "../Modal/Modal.web";
import type {
  DrawerContentProps,
  DrawerHeaderSharedProps,
  DrawerOverlayProps,
  DrawerPositionerProps,
  DrawerSharedProps,
} from "./Drawer.props";

type DrawerProps = DrawerSharedProps &
  Omit<ChakraDrawerRootProps, "children" | "open" | "onOpenChange"> & {
    // v1/v2 compatibility
    isOpen?: boolean;
    open?: boolean;
    closeOnEsc?: boolean;
    closeOnEscape?: boolean;
    closeOnOverlayClick?: boolean;
    closeOnInteractOutside?: boolean;
    blockScrollOnMount?: boolean;
    preventScroll?: boolean;

    size?: ChakraDrawerRootProps["size"];
    placement?: ChakraDrawerRootProps["placement"] | "left" | "right";
    id?: string;
  };

type DrawerHeaderProps = DrawerHeaderSharedProps & ChakraDrawerHeaderProps;

// Interface for Drawer component with all static properties
interface DrawerComponent {
  (props: DrawerProps): JSX.Element;
  Root: typeof ChakraDrawer.Root;
  BaseDrawer: typeof ChakraDrawer.Root;
  Positioner: React.ForwardRefExoticComponent<
    DrawerPositionerProps & React.RefAttributes<HTMLDivElement>
  >;
  Backdrop: React.ForwardRefExoticComponent<
    DrawerOverlayProps & React.RefAttributes<HTMLDivElement>
  >;
  Portal: typeof Portal;
  RawContent: React.ForwardRefExoticComponent<
    DrawerContentProps & React.RefAttributes<HTMLDivElement>
  >;
  Overlay: (props: DrawerOverlayProps) => JSX.Element;
  Content: (props: DrawerContentProps) => JSX.Element;
  Header: (props: DrawerHeaderProps) => JSX.Element;
  Body: (props: ChakraDrawerBodyProps) => JSX.Element;
  Footer: (props: ChakraDrawerFooterProps) => JSX.Element;
  CloseButton: (
    props: ComponentProps<typeof CloseButton> &
      Omit<ChakraDrawerCloseTriggerProps, "asChild" | "children">
  ) => JSX.Element;
}

function Drawer({
  children,
  placement = "bottom",
  drawerContentProps,
  variant = "informational",
  onClose,
  isOpen,
  open,
  closeOnEsc,
  closeOnEscape,
  closeOnOverlayClick,
  closeOnInteractOutside,
  blockScrollOnMount,
  preventScroll,
  size,
  id,
  ...props
}: DrawerProps): JSX.Element {
  const reactId = useId();
  const resolvedId = id ?? reactId;

  const Positioner = ChakraDrawer.Positioner as React.FC<DrawerPositionerProps>;
  const Content = ChakraDrawer.Content as React.FC<DrawerContentProps>;
  let resolvedPlacement: ChakraDrawerRootProps["placement"] = "bottom";
  if (placement === "left") {
    resolvedPlacement = "start";
  } else if (placement === "right") {
    resolvedPlacement = "end";
  } else {
    resolvedPlacement = placement;
  }
  const openValue = open !== undefined ? open : isOpen;
  const closeOnEscapeValue =
    closeOnEscape !== undefined ? closeOnEscape : closeOnEsc;
  const closeOnOverlayValue =
    closeOnInteractOutside !== undefined
      ? closeOnInteractOutside
      : closeOnOverlayClick;
  const preventScrollValue =
    preventScroll !== undefined ? preventScroll : blockScrollOnMount;

  const [overlay, originalHeader, body, footer, customContent, otherChildren] =
    extractSlots(children);

  const header = isValidElement(originalHeader)
    ? cloneElement(originalHeader as ReactElement<DrawerHeaderProps>, {
        onClose:
          (originalHeader as ReactElement<DrawerHeaderProps>).props.onClose ??
          onClose,
      })
    : null;

  // If a custom Content component is provided, use it instead of the default
  const hasCustomContent = customContent !== null;

  // Extract zIndex from drawerContentProps to apply to Positioner
  const { zIndex, ...restContentProps } = drawerContentProps || {};

  return (
    <ChakraDrawer.Root
      placement={resolvedPlacement}
      open={openValue}
      closeOnEscape={closeOnEscapeValue}
      closeOnInteractOutside={closeOnOverlayValue}
      preventScroll={preventScrollValue}
      size={size}
      onOpenChange={({ open: nextOpen }: { open: boolean }) => {
        if (nextOpen === false) {
          onClose?.();
        }
      }}
      id={resolvedId}
      {...props}
    >
      {overlay ?? (
        <Portal>
          <ChakraDrawer.Backdrop />
        </Portal>
      )}
      {otherChildren}
      {hasCustomContent ? (
        customContent
      ) : (
        <Portal>
          <Positioner zIndex={zIndex}>
            <Content
              borderTopLeftRadius={
                resolvedPlacement === "bottom" ? "sm" : undefined
              }
              borderTopRightRadius={
                resolvedPlacement === "bottom" ? "sm" : undefined
              }
              overflow="hidden"
              {...restContentProps}
            >
              {header}
              {isValidElement(body)
                ? cloneElement(body as ReactElement<ChakraDrawerBodyProps>, {
                    bg: bodyBackgroundColor(variant),
                  })
                : body}
              {footer}
            </Content>
          </Positioner>
        </Portal>
      )}
    </ChakraDrawer.Root>
  );
}

// Cast for internal use and external consumers
const DrawerTyped = Drawer as DrawerComponent;

// Wrapper for ChakraDrawer.Root that handles v2 → v3 prop conversion
function BaseDrawerWrapper({
  isOpen,
  open,
  onClose,
  closeOnEsc,
  closeOnEscape,
  closeOnOverlayClick,
  closeOnInteractOutside,
  blockScrollOnMount,
  preventScroll,
  placement = "bottom",
  id,
  ...props
}: ChakraDrawerRootProps & {
  isOpen?: boolean;
  onClose?: () => void;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  blockScrollOnMount?: boolean;
  placement?: ChakraDrawerRootProps["placement"] | "left" | "right";
  id?: string;
}): JSX.Element {
  const reactId = useId();
  const resolvedId = id ?? reactId;

  const openValue = open !== undefined ? open : isOpen;
  const closeOnEscapeValue =
    closeOnEscape !== undefined ? closeOnEscape : closeOnEsc;
  const closeOnOverlayValue =
    closeOnInteractOutside !== undefined
      ? closeOnInteractOutside
      : closeOnOverlayClick;
  const preventScrollValue =
    preventScroll !== undefined ? preventScroll : blockScrollOnMount;

  let resolvedPlacement: ChakraDrawerRootProps["placement"] = "bottom";
  if (placement === "left") {
    resolvedPlacement = "start";
  } else if (placement === "right") {
    resolvedPlacement = "end";
  } else {
    resolvedPlacement = placement;
  }

  return (
    <ChakraDrawer.Root
      open={openValue}
      closeOnEscape={closeOnEscapeValue}
      closeOnInteractOutside={closeOnOverlayValue}
      preventScroll={preventScrollValue}
      placement={resolvedPlacement}
      onOpenChange={({ open: nextOpen }: { open: boolean }) => {
        if (nextOpen === false) {
          onClose?.();
        }
      }}
      id={resolvedId}
      {...props}
    />
  );
}

// Static property assignments with proper typing
// Cast to extended types to include children and style props that Chakra's types don't expose
DrawerTyped.Root = ChakraDrawer.Root;
DrawerTyped.BaseDrawer = BaseDrawerWrapper as typeof ChakraDrawer.Root;
DrawerTyped.Positioner =
  ChakraDrawer.Positioner as React.ForwardRefExoticComponent<
    DrawerPositionerProps & React.RefAttributes<HTMLDivElement>
  >;
// Custom Backdrop that supports onClick by wrapping in a clickable Box
const BackdropWithClick = React.forwardRef<HTMLDivElement, DrawerOverlayProps>(
  (
    { onClick, css: cssProp, zIndex, top, left, right, bottom, ...props },
    ref
  ) => {
    const Backdrop = ChakraDrawer.Backdrop as React.FC<
      DrawerOverlayProps & { ref?: React.Ref<HTMLDivElement> }
    >;

    if (onClick) {
      // Wrap backdrop in a clickable container with position props
      // Inner Backdrop must be absolute (not fixed) to fill wrapper, not viewport
      return (
        <Box
          ref={ref}
          onClick={onClick}
          position="fixed"
          zIndex={zIndex}
          top={top ?? 0}
          left={left ?? 0}
          right={right ?? 0}
          bottom={bottom ?? 0}
        >
          <Backdrop
            {...props}
            css={{
              pointerEvents: "none",
              position: "absolute",
              inset: 0,
            }}
          />
        </Box>
      );
    }

    return <Backdrop ref={ref} zIndex={zIndex} css={cssProp} {...props} />;
  }
);
BackdropWithClick.displayName = "Drawer.Backdrop";
DrawerTyped.Backdrop = BackdropWithClick;
DrawerTyped.Portal = Portal;
// Raw content without Portal wrapper - use with Drawer.Portal manually
DrawerTyped.RawContent =
  ChakraDrawer.Content as React.ForwardRefExoticComponent<
    DrawerContentProps & React.RefAttributes<HTMLDivElement>
  >;

DrawerTyped.Overlay = function Overlay({
  backdropFilter,
  ...props
}: DrawerOverlayProps): JSX.Element {
  const Backdrop = ChakraDrawer.Backdrop as React.FC<DrawerOverlayProps>;
  return (
    <Portal>
      <Backdrop
        {...props}
        {...(backdropFilter && { style: { ...props.style, backdropFilter } })}
      />
    </Portal>
  );
};

DrawerTyped.Content = function Content({
  bg = "bg",
  ...props
}: DrawerContentProps): JSX.Element {
  const Positioner = ChakraDrawer.Positioner as React.FC<{
    children: ReactNode;
  }>;
  const Content = ChakraDrawer.Content as React.FC<DrawerContentProps>;

  return (
    <Portal>
      <Positioner>
        <Content bg={bg} {...props} />
      </Positioner>
    </Portal>
  );
};

DrawerTyped.Header = function Header({
  children,
  onClose: onCloseProp,
  closeButtonProps,
  ...props
}: DrawerHeaderProps): JSX.Element {
  const { t } = useTranslation("common");

  const getCloseButton = (
    config: DrawerHeaderSharedProps["closeButtonProps"]
  ) => {
    return (
      onCloseProp && (
        <IconButton.Ghost
          onClick={onCloseProp}
          iconName="X"
          aria-label={t("Close")}
          size="xs"
          iconSize="element.xl"
          data-testid="drawer-close-button"
          colorPalette="link"
          iconColor={config?.iconColor ?? "fg"}
          {...config}
        />
      )
    );
  };

  return (
    <ChakraDrawer.Header
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      fontSize={{ base: "md", sm: "xl" }}
      px={{ base: "md", sm: "lg" }}
      py={{ base: "md", sm: "lg" }}
      position="relative"
      color="fg"
      bg="bg"
      borderBottom="1px"
      borderColor="border"
      data-testid="drawer-header"
      fontWeight="bold"
      {...props}
    >
      <>{children}</>
      {getCloseButton({ iconColor: "font.dark", ...closeButtonProps })}
    </ChakraDrawer.Header>
  );
};

DrawerTyped.Body = function Body(props: ChakraDrawerBodyProps): JSX.Element {
  return <ChakraDrawer.Body {...props} />;
};

DrawerTyped.Footer = function Footer(
  props: ChakraDrawerFooterProps
): JSX.Element {
  return <ChakraDrawer.Footer color="fg" bg="bg" {...props} />;
};

DrawerTyped.CloseButton = function DrawerCloseButton(
  props: ComponentProps<typeof CloseButton> &
    Omit<ChakraDrawerCloseTriggerProps, "asChild" | "children">
): JSX.Element {
  // Type cast needed because Chakra v3 types don't properly expose children/asChild props.
  const CloseTrigger = ChakraDrawer.CloseTrigger as React.FC<
    ChakraDrawerCloseTriggerProps & { children: ReactNode; asChild?: boolean }
  >;

  return (
    <CloseTrigger position="absolute" top="md" insetEnd="md" {...props} asChild>
      <CloseButton tabIndex={-1} {...props} />
    </CloseTrigger>
  );
};

// Helper to check if a component type matches, including styled wrappers
function isComponentType(
  childType: unknown,
  targetComponent: unknown,
  targetDisplayName: string
): boolean {
  if (childType === targetComponent) return true;
  // Check for styled-components/emotion wrappers which have a different type
  // but may wrap the target component
  if (typeof childType === "object" && childType !== null) {
    const ct = childType as Record<string, unknown>;
    // Check displayName for styled components
    if (
      typeof ct.displayName === "string" &&
      ct.displayName.includes(targetDisplayName)
    ) {
      return true;
    }
    // Check emotion's base component (emotion stores it with double underscore prefix)
    // eslint-disable-next-line no-underscore-dangle
    const emotionBase = (ct as { __emotion_base?: unknown }).__emotion_base;
    if (emotionBase === targetComponent) return true;
  }
  return false;
}

function extractSlots(children: DrawerProps["children"]) {
  const otherChildren: Array<ReactNode> = [];
  let footer: ReactNode = null;
  let header: ReactNode = null;
  let overlay: ReactNode = null;
  let body: ReactNode = null;
  let content: ReactNode = null;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      otherChildren.push(child);
    } else if (isComponentType(child.type, DrawerTyped.Footer, "Footer")) {
      footer = child;
    } else if (isComponentType(child.type, DrawerTyped.Header, "Header")) {
      header = child;
    } else if (isComponentType(child.type, DrawerTyped.Overlay, "Overlay")) {
      overlay = child;
    } else if (isComponentType(child.type, DrawerTyped.Body, "Body")) {
      body = child;
    } else if (isComponentType(child.type, DrawerTyped.Content, "Content")) {
      content = child;
    } else {
      otherChildren.push(child);
    }
  });

  return [overlay, header, body, footer, content, otherChildren] as const;
}

export default DrawerTyped;
